import { useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo } from 'react';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import {
	getSyncCooldownRemaining,
	type SyncStatusCache,
	useSyncInventoryMutation,
} from '../api/useSyncInventory';

/**
 * Categorized inventory items for sync operation
 */
export interface CategorizedSyncItems {
	/** Items ready to sync (have vehicleDetailsUrl and not on cooldown) */
	ready: IInventoryItem[];
	/** Items missing vehicleDetailsUrl */
	missingUrl: IInventoryItem[];
	/** Items on cooldown (synced within the last hour) */
	onCooldown: Array<{
		item: IInventoryItem;
		remainingMs: number;
	}>;
}

/**
 * Hook for managing sync operations on selected inventory items
 * Provides categorization and fire-and-forget sync execution
 */
export const useSyncSelectedInventory = () => {
	const queryClient = useQueryClient();
	const syncMutation = useSyncInventoryMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	/**
	 * Categorize items into ready, missingUrl, and onCooldown groups
	 */
	const categorizeSyncItems = useCallback(
		(items: IInventoryItem[]): CategorizedSyncItems => {
			const result: CategorizedSyncItems = {
				ready: [],
				missingUrl: [],
				onCooldown: [],
			};

			for (const item of items) {
				// Check for vehicleDetailsUrl or scrapeUrl (required for syncing)
				const vehicleDetailsUrl =
					item.vehicleDetailsUrl || item.scrapingConfig?.INVENTORY_ITEM_SYNC?.scrapeUrl;
				if (!vehicleDetailsUrl) {
					result.missingUrl.push(item);
					continue;
				}

				// Check for cooldown (sync status cache is fine to read directly)
				const syncStatus = queryClient.getQueryData<SyncStatusCache>(
					queries.inventory.syncStatus(item.id).queryKey,
				);
				const remainingMs = getSyncCooldownRemaining(syncStatus);

				if (remainingMs > 0) {
					result.onCooldown.push({ item, remainingMs });
					continue;
				}

				// Item is ready to sync
				result.ready.push(item);
			}

			return result;
		},
		[queryClient],
	);

	/**
	 * Execute sync for items
	 * Categorizes and syncs ready items
	 * Returns the count of items that sync was initiated for
	 */
	const syncItemsByIds = useCallback(
		async (itemIds: Set<string>, items: IInventoryItem[]): Promise<number> => {
			if (!currentServicesProviderId || itemIds.size === 0 || items.length === 0) {
				return 0;
			}

			// Categorize items
			const categorized = categorizeSyncItems(items);
			const readyItems = categorized.ready;

			if (readyItems.length === 0) {
				return 0;
			}

			// Set sync status immediately for all items to prevent duplicate syncs (cached/persisted)
			readyItems.forEach((item) => {
				queryClient.setQueryData<SyncStatusCache>(
					queries.inventory.syncStatus(item.id).queryKey,
					{
						id: item.id,
						syncedAt: Date.now(),
						success: true,
					},
				);
			});

			// Fire-and-forget: don't await, just start the mutation with all inventory IDs
			syncMutation.mutate({
				servicesProviderId: currentServicesProviderId,
				inventoryIds: readyItems.map((item) => item.id),
			});

			return readyItems.length;
		},
		[categorizeSyncItems, syncMutation, currentServicesProviderId, queryClient],
	);

	/**
	 * Get the maximum remaining cooldown time across all cooldown items
	 */
	const getMaxCooldownRemaining = useCallback((categorized: CategorizedSyncItems): number => {
		if (categorized.onCooldown.length === 0) {
			return 0;
		}
		return Math.max(...categorized.onCooldown.map((c) => c.remainingMs));
	}, []);

	return useMemo(
		() => ({
			syncItemsByIds,
			getMaxCooldownRemaining,
		}),
		[syncItemsByIds, getMaxCooldownRemaining],
	);
};
